/* global fetchVenuePositionsDataIndexUrl */

import React, { Fragment, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';
import { format } from 'date-fns';

import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import useApiRequest from 'Hooks/useApiRequest';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';

const LOCAL_STORAGE_KEY = 'WORKFORCE_DETAILS_EXPANDED';

const getPersistedExpanded = () => {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {};
};

function VenueWorkforceDetails({ fetchVenuePositionsDataIndexUrl }) {
    const { data: venuePositions, get: getVenuePositions, loading: loadingVenuePositions } = useApiRequest({ url: fetchVenuePositionsDataIndexUrl });
    const [expanded, setExpanded] = useState(getPersistedExpanded);

    useEffect(() => {
        getVenuePositions();
    }, []);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(expanded));
    }, [expanded]);

    const hasDepartments = !!Object.keys(venuePositions?.departments || {}).length;

    const toggleExpanded = (key) => {
        setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const expandAll = () => {
        if (!venuePositions?.departments) return;
        const next = {};
        Object.entries(venuePositions.departments).forEach(([departmentId, department]) => {
            next[departmentId] = true;
            Object.keys(department.costcenters || {}).forEach((costcenterId) => {
                next[costcenterId] = true;
            });
        });
        setExpanded(next);
    };

    const collapseAll = () => {
        setExpanded({});
    };

    return (
        <div className="table__card tw-flex tw-flex-col tw-h-full tw-min-h-0">
            <div className="tw-flex tw-flex-col">
                <h4 className="table__header">{$t('Workforce Details')}</h4>
                {!loadingVenuePositions && hasDepartments && (
                    <div className="tw-flex tw-justify-between tw-mb-6">
                        <div className="tw-py-1 tw-flex tw-gap-x-2 tw-items-baseline">
                            <div className="tw-text-xs tw-font-medium tw-text-charcoal-50">{$t('Row view')}</div>
                            <div className="tw-flex tw-gap-x-1.5">
                                <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={expandAll}>
                                    {$t('Expand all')}
                                </button>
                                <button className="tw-text-xs tw-font-medium tw-text-charcoal tw-underline" onClick={collapseAll}>
                                    {$t('Collapse all')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="inbud-table_wrapper tw-min-h-0 tw-overflow-y-scroll">
                <table className="inbud-table tw-table-fixed">
                    <thead className="tw-sticky tw-top-0 tw-z-10">
                        <tr>
                            <th className="">{$t('Position Description')}</th>
                            <th className="tw-w-28">{$t('Start date')}</th>
                            <th className="tw-w-28">{$t('End date')}</th>
                            <th className="tw-w-32">{$t('Full Name')}</th>
                            <th className="tw-w-28">% {$t('Allocation')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loadingVenuePositions && <TableRowSkeleton rows={10} columns={5} />}
                        {!loadingVenuePositions && !hasDepartments && (
                            <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                <td colSpan="5" className="tw-p-4">
                                    <span>{$t('No Positions found')}</span>
                                </td>
                            </tr>
                        )}
                        {!loadingVenuePositions &&
                            hasDepartments &&
                            Object.entries(venuePositions?.departments).map(([departmentId, department]) => (
                                <VenueWorkforceDetailsTableGroupedRow
                                    key={departmentId}
                                    name={department.name}
                                    show={!!expanded[departmentId]}
                                    onToggle={() => toggleExpanded(departmentId)}
                                >
                                    {Object.entries(department.costcenters || {}).map(([costcenterId, costcenter]) => (
                                        <VenueWorkforceDetailsTableGroupedRow
                                            key={costcenterId}
                                            name={costcenter.name}
                                            indent={1}
                                            show={!!expanded[costcenterId]}
                                            onToggle={() => toggleExpanded(costcenterId)}
                                        >
                                            {costcenter.positions.map((position) => (
                                                <VenueWorkforceDetailsTableRow key={position.id} position={position} indent={2} />
                                            ))}
                                        </VenueWorkforceDetailsTableGroupedRow>
                                    ))}
                                    {department.positions.map((position) => (
                                        <VenueWorkforceDetailsTableRow key={position.id} position={position} />
                                    ))}
                                </VenueWorkforceDetailsTableGroupedRow>
                            ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function VenueWorkforceDetailsTableGroupedRow({ name, children, indent = 0, show, onToggle }) {
    return (
        <Fragment>
            <tr>
                <td colSpan="5" className="tw-font-bold tw-cursor-pointer !tw-border-r-0" onClick={onToggle}>
                    <div className="tw-flex tw-items-center tw-gap-x-1.5">
                        <div style={{ paddingLeft: `${indent * 1.5}rem` }}>{name}</div>
                        <div>{show ? <ChevronUpIcon /> : <ChevronDownIcon />}</div>
                    </div>
                </td>
            </tr>
            {show && children}
        </Fragment>
    );
}

function VenueWorkforceDetailsTableRow({ position, indent = 1 }) {
    return (
        <tr className="!tw-bg-white">
            <td>
                <div style={{ paddingLeft: `${indent * 1.5}rem` }}>{position.name}</div>
            </td>
            <td className="inbud-date-cell tw-text-center">{format(new Date(position.start_date), 'dd/MM/yyyy')}</td>
            <td className="inbud-date-cell tw-text-center">{format(new Date(position.end_date), 'dd/MM/yyyy')}</td>
            <td>{position.budgeted_staff_name || $t('Not yet recruited')}</td>
            <td className="inbud-numeric-cell">{position.allocation}%</td>
        </tr>
    );
}

if (document.getElementById('VenueWorkforceDetails')) {
    var rootEl = document.getElementById('VenueWorkforceDetails');
    ReactDOM.render(
        <App>
            <VenueWorkforceDetails fetchVenuePositionsDataIndexUrl={fetchVenuePositionsDataIndexUrl} />
        </App>,
        rootEl,
    );
}
